import type { ZodError } from "zod";
import { AuthError } from "@supabase/supabase-js";
import { ApiError } from "./api";

/**
 * Turning a thrown thing into a sentence a technician can act on.
 *
 * The rule is the same as everywhere else: **branch on a code, never on message
 * text.** Supabase exposes `error.code`; our API exposes `error.code`. Both are
 * contracts. Matching on the prose is how a provider's copy edit silently breaks
 * your error handling.
 *
 * Supabase's own messages are also written for developers - "Invalid login
 * credentials", "Email not confirmed" - so the ones users actually hit are
 * rewritten here.
 */
export function describeAuthError(err: unknown): string {
  if (err instanceof AuthError) {
    switch (err.code) {
      case "invalid_credentials":
        return "That email and password don't match. Check both, or continue with Google, Microsoft or Apple if that is how you signed up.";
      case "email_not_confirmed":
        return "Confirm your email address first - check your inbox for the link.";
      case "user_already_exists":
      case "email_exists":
        return "An account with that email already exists. Try signing in instead.";
      case "signup_disabled":
        return "Sign-up is turned off here. Ask an administrator to invite you.";
      case "provider_disabled":
      case "provider_email_needs_verification":
        // Almost always a deployment mismatch rather than anything the user did:
        // the button rendered, but config.toml has not enabled that provider.
        // Say what happened, and point them at the way in that definitely works.
        return "That sign-in method is not available here yet. Use your email and password.";
      case "weak_password":
        return "That password is too easy to guess. Use at least 10 characters.";
      case "over_request_rate_limit":
      case "over_email_send_rate_limit":
        return "Too many attempts. Wait a minute and try again.";
      case "captcha_failed":
        return "Something went wrong. Please try again in a moment.";
      case "same_password":
        return "That is already your current password. Choose a different one.";
      // What `updateUser`'s `current_password` field returns, not
      // `reauthentication_not_valid` - that code is for the separate
      // nonce-based `reauthenticate()` flow, which this product does not use.
      case "current_password_invalid":
        return "Your current password is incorrect. Try again.";
      case "current_password_required":
        return "Enter your current password.";
      case "session_expired":
      case "refresh_token_not_found":
        return "Your session has expired. Sign in again.";
      default:
        // A 4xx from Supabase is still the user's problem to act on, so its
        // message is at least directionally useful; a 5xx is ours and its
        // internals help nobody.
        return err.status && err.status < 500
          ? err.message
          : "We couldn't reach the sign-in service. Try again in a moment.";
    }
  }

  if (err instanceof ApiError) {
    if (err.isNetwork) return "Could not reach the server. Check your connection.";
    return err.message;
  }

  return "Something went wrong. Try again.";
}

/**
 * zod issues → `{ fieldName: firstMessage }` for rendering under each input.
 *
 * Only the first message per field: showing three complaints about one password
 * box is noise, and the user can only fix one at a time anyway.
 */
export function fieldErrorsFrom(error: ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path[0];
    if (typeof key === "string" && !(key in out)) out[key] = issue.message;
  }
  return out;
}
